import { useEffect, useState } from "react";
import { Star, MessageSquareText } from "lucide-react";
import apiClient from "@/lib/api";
import StarRating from "@/components/review/StarRating";
import { BREAKDOWN_ROWS, formatReviewDate } from "@/components/review/reviewMeta";

export default function PropertyReviews({ propertyId }) {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/properties/${propertyId}/reviews`)
      .then((r) => {
        setSummary(r.data?.summary || null);
        setReviews(r.data?.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [propertyId]);

  const count = summary?.review_count || 0;
  const bd = summary?.breakdown || {};

  if (loading) return <p className="text-sm text-muted-foreground">Memuat ulasan…</p>;

  if (count === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="detail-reviews-empty">
        Belum ada ulasan untuk hunian ini.
      </p>
    );
  }

  return (
    <div data-testid="detail-reviews">
      {/* Quality breakdown */}
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="text-center sm:text-left shrink-0">
          <div className="font-heading text-4xl font-bold text-foreground" data-testid="detail-reviews-overall">
            {(bd.overall || 0).toFixed(1)}
          </div>
          <div className="mt-1 flex justify-center sm:justify-start">
            <StarRating value={Math.round(bd.overall || 0)} readOnly size={16} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="detail-reviews-count">{count} ulasan</p>
        </div>
        <div className="flex-1 space-y-2">
          {BREAKDOWN_ROWS.map((row) => {
            const v = bd[row.key] || 0;
            return (
              <div key={row.key} className="flex items-center gap-3" data-testid={`detail-breakdown-${row.key}`}>
                <span className="w-24 text-sm text-muted-foreground">{row.label}</span>
                <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-amber-400" style={{ width: `${(v / 5) * 100}%` }} />
                </div>
                <span className="w-8 text-right text-sm font-semibold">{v.toFixed(1)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review list */}
      <div className="mt-6 space-y-4" data-testid="detail-reviews-list">
        {reviews.map((rev, i) => (
          <div key={rev.id} data-testid={`detail-review-${i}`} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="font-semibold text-foreground text-sm">{rev.customer_name || "Pengguna AVASA"}</span>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-500">
                  <Star size={14} fill="currentColor" /> {rev.overall_rating}
                </span>
                <span className="text-xs text-muted-foreground">{formatReviewDate(rev.created_at)}</span>
              </div>
            </div>
            {rev.comment && (
              <p className="mt-2 text-sm text-foreground/90 leading-relaxed flex gap-2">
                <MessageSquareText size={15} className="shrink-0 mt-0.5 text-muted-foreground" /> {rev.comment}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
